import { Box, BoxProps, ColorOptions, ModalDialog, TextInputNewProps, TextNewProps } from '@jfs/ui-new'
import { TextInputNew } from '../../../../../components/AdminInputs'
import { ButtonNew } from '../../../../../components/AdminButton'
import { useCallback, useEffect, useState } from 'react'
import { DropdownStateless } from '../../../../../components/DropdownStateless'
import { dateFormatter, DateFormatTypes } from '../../../../../utils'

const getFormattedHistory = (changedHistory, fieldName, ddData) => {
  if (changedHistory?.[fieldName] && changedHistory?.[fieldName]?.length > 0) {
    const lastChangedHistory = changedHistory?.[fieldName]?.at(-1)
    if (lastChangedHistory?.oldValue) {
      return (
        'New Value : ' +
        (ddData?.find((item) => item?.value === lastChangedHistory?.newValue)?.description ??
          extraFieldsDesc[lastChangedHistory?.newValue] ??
          lastChangedHistory?.newValue) +
        '||' +
        'Old Value : ' +
        (ddData?.find((item) => item?.value === lastChangedHistory?.oldValue)?.description ??
          extraFieldsDesc[lastChangedHistory?.oldValue] ??
          lastChangedHistory?.oldValue) +
        '||' +
        'Updated  At : ' +
        dateFormatter(lastChangedHistory?.updatedAt, DateFormatTypes.DATE_AM_PM) +
        '||' +
        'Updated  By : ' +
        lastChangedHistory?.user?.username
      )
    }
  }
  return undefined
}

export const DropdownWithInfo = (props: any) => {
  const { defaultEditableValue, shouldShowCustomInputField, minCustomVal } = props
  const [customValue, SetCustomValue] = useState('')
  const [showCustomInputField, setShowCustomInputField] = useState(false)

  useEffect(() => {
    if (defaultEditableValue && !props.value && props.selectable) {
      props.setFieldValue(props.fieldName, defaultEditableValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultEditableValue, props.selectable, !!props.value])

  const onDismiss = useCallback(() => {
    SetCustomValue('')
    setShowCustomInputField(false)
  }, [])

  const onCustomValueChange = useCallback((text) => {
    if (!/^\d+$/.test(text)) {
      text = text.slice(0, text.length - 1)
    }
    SetCustomValue(text)
  }, [])

  const onCustomValueSubmit = useCallback(() => {
    props?.setFieldValue(props?.fieldName, customValue)
    setShowCustomInputField(false)
    SetCustomValue('')
  }, [customValue, props])

  const onDDValChange = useCallback(
    (item) => {
      if (shouldShowCustomInputField?.(item)) {
        props?.setFieldValue(props?.fieldName, '')
        setShowCustomInputField(true)
      }
    },
    [props, shouldShowCustomInputField],
  )

  return (
    <>
      <DropdownStateless
        {...props}
        ignoreTouched
        selectedData={onDDValChange}
        error={defaultEditableValue ? undefined : props.error}
        modalDetails={{ data: getFormattedHistory(props?.allValues?.changeHistory, props?.fieldName, props?.data) }}
      />
      <CustomInputModel
        showCustomInputField={showCustomInputField}
        onDismiss={onDismiss}
        disableCustomFieldSubmit={minCustomVal && customValue <= minCustomVal}
        customValue={customValue}
        onCustomValueChange={onCustomValueChange}
        onCustomValueSubmit={onCustomValueSubmit}
        title={`Enter Custom Value for ${props?.label} greater than ${minCustomVal}`}
      />
    </>
  )
}

const extraFieldsDesc = {
  Y: 'yes',
  N: 'No',
}

interface CustomInputModelProps {
  showCustomInputField: boolean
  onDismiss: any
  disableCustomFieldSubmit: any
  customValue: any
  onCustomValueChange: any
  onCustomValueSubmit: any
  title: any
}

const CustomInputModel = ({
  showCustomInputField,
  disableCustomFieldSubmit,
  onDismiss,
  customValue,
  onCustomValueChange,
  onCustomValueSubmit,
  title,
}: CustomInputModelProps) => {
  return (
    <ModalDialog type="Centered" show={showCustomInputField} underlayBackground="TRANSPARENT">
      <Box {...styles.container}>
        <Box {...styles.header}>
          <ButtonNew
            title={'Close'}
            onPress={onDismiss}
            height={30}
            color={'ERROR'}
            borderRadius={4}
            underlayColor={'TRANSPARENT'}
            hoverColor={'ERROR'}
          />
        </Box>
        <Box {...styles.listContainer}>
          <TextInputNew value={customValue} label={title} onChangeText={onCustomValueChange} {...styles.textinput} />

          <Box align={'flex-end'} mt={16}>
            <ButtonNew
              title={'Update'}
              underlayColor={'TRANSPARENT'}
              onPress={onCustomValueSubmit}
              height={36}
              color={'GREEN'}
              borderRadius={4}
              width={96}
              disabled={disableCustomFieldSubmit}
            />
          </Box>
        </Box>
      </Box>
    </ModalDialog>
  )
}

const shadow = {
  shadowColor: '#000',
  shadowOffset: {
    width: 1,
    height: 1,
  },
  shadowOpacity: 0.1,
  shadowRadius: 4,
  zIndex: 10,
}

const styles = {
  container: {
    bg: 'TRANSPARENT',
    w: '50%',
    alignSelf: 'center',
    marginLeft: '20%',
  } as BoxProps,
  header: {
    backgroundColor: '#f8f9fc' as any,
    ...shadow,
    underlayColor: 'WHITE',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: 14,
    borderWidth: 1,
    borderRadius: 4,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    borderColor: '#e3e6f0' as ColorOptions,
  } as BoxProps,
  headerText: {
    fontFamily: 'jiotype-bold',
    color: '#214796' as ColorOptions,
    fontSize: 16,
  } as TextNewProps,
  closeIcon: {
    backgroundColor: 'WHITE',
    borderRadius: 32,
    p: 4,
  } as BoxProps,
  listContainer: {
    ...shadow,
    backgroundColor: 'WHITE',
    padding: 16,
    underlayColor: 'WHITE',
    borderColor: '#e3e6f0' as ColorOptions,
    borderWidth: 1,
    borderTopWidth: 0,
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
  } as BoxProps,
  textinput: {
    type: 'body14',
    borderColor: '#d1d3e2' as ColorOptions,
    labelPaddingBottom: 8,
    borderRadius: 4,
    labelFontFamily: 'jiotype-bold',
    fontFamily: 'jiotype-medium',
    labelColor: '#6e707e' as ColorOptions,
    labelFontSize: 12,
    fontSize: 14,
    selectionColor: 'BLUE',
    paddingHorizontal: 8,
    color: '#6e707e' as ColorOptions,
    paddingVertical: 8,
    focusBorderColor: '#d1d3e2' as ColorOptions,
    borderWidth: 2,
    onFocusBorderWidth: 2,
  } as TextInputNewProps,
}
